import { Empty, Spin, Tree } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { OriginRoleItem } from './type';

import { getTreeList } from './request';
import styles from './RoleTree.less';
import type { TreeNode } from './TreeNode';
import { DataTree } from './TreeNode';

import type { SelectRole } from './type';

interface RoleTreeProps {
  groupNodes: SelectRole[];
  setGroupNodes: (groupNode: SelectRole[]) => void;
  searchValue?: string;
  needCustom: boolean;
}

const getAllDataList = (tree: TreeNode[]): TreeNode[] => {
  const result: TreeNode[] = [];

  tree.forEach((item) => {
    result.push(item);
    if (item.hasChildren) {
      result.push(...getAllDataList(item.childNodes));
    }
  });
  return result;
};

const RoleTree = ({ groupNodes, setGroupNodes, searchValue, needCustom }: RoleTreeProps) => {
  const [roleTreeData, setRoleTreeData] = useState<OriginRoleItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [selectedId, setSelectedId] = useState<string>();

  const dataTree = useMemo(() => {
    return new DataTree(roleTreeData, false);
  }, [roleTreeData]);

  const dataTreeList = useMemo(() => {
    return getAllDataList(dataTree.nodes)
      .filter((item) => item.name.toLowerCase().indexOf(searchValue?.toLowerCase() || '') > -1)
      .map((elem) => {
        const strTitle = elem.name;
        const index = strTitle.toLowerCase().indexOf(searchValue?.toLowerCase() || '');
        const beforeStr = strTitle.substring(0, index);
        const afterStr = strTitle.slice(index + (searchValue || '').length);
        const name =
          index > -1 ? (
            <span>
              {beforeStr}
              <span className={styles.siteTreeSearchValue}>
                {strTitle.slice(index, index + (searchValue || '').length)}
              </span>
              {afterStr}
            </span>
          ) : (
            <span>{strTitle}</span>
          );
        return {
          key: elem.key,
          title: name as React.ReactNode,
        };
      });
  }, [dataTree.nodes, searchValue]);

  const groupIds = useMemo(() => groupNodes.map((node) => String(node.id)), [groupNodes]);

  const checkedKeysValue = useMemo(() => {
    const checkedKeys: string[] = [];
    groupNodes.forEach((item) => {
      const nodeItem = dataTree.getNode(String(item.id));
      if (nodeItem) {
        checkedKeys.push(...[...nodeItem.flattenChildNodes, nodeItem].map((elem) => elem.key));
      }
    });

    return checkedKeys;
  }, [dataTree, groupNodes]);

  const updateGroupNode = (ids: string[]) => {
    const node: SelectRole[] = [];
    ids.forEach((item) => {
      const nodeItem = dataTree.getNode(item);
      if (nodeItem) {
        node.push({
          id: nodeItem.id,
          name: nodeItem.name,
          completeName: nodeItem.completeName,
        });
      }
    });
    setGroupNodes(node);
  };

  const getTreeListRun = useCallback(
    () =>
      getTreeList().then((data) => {
        setRoleTreeData(needCustom ? data.data : data.data.filter((item) => item.id !== 4));
        setIsLoading(false);
      }),
    [],
  );

  useEffect(() => {
    getTreeListRun();
  }, []);

  return isLoading ? (
    <div className={styles.empty}>
      <Spin tip="Loading" size="default">
        <div className="content" />
      </Spin>
    </div>
  ) : (searchValue && dataTreeList.length > 0) || (!searchValue && dataTree.data.length > 0) ? (
    <Tree
      checkable
      blockNode
      checkStrictly
      selectedKeys={[selectedId || '']}
      expandedKeys={expandedKeys}
      autoExpandParent={autoExpandParent}
      onExpand={(newExpandedKeys) => {
        setExpandedKeys(newExpandedKeys);
        setAutoExpandParent(false);
      }}
      checkedKeys={{
        checked: checkedKeysValue || [],
        halfChecked: [],
      }}
      treeData={searchValue ? dataTreeList : dataTree.data}
      onSelect={(_selectedKeys, { selected, node }) => {
        if (selected) {
          setExpandedKeys([...expandedKeys, node.key]);
          setSelectedId(node.key);
        } else {
          setExpandedKeys(expandedKeys.filter((item) => item !== node.key));
          setSelectedId(void 0);
        }
      }}
      onCheck={(_checkedKeys, { node: { key, checked } }) => {
        const node = dataTree.getNode(key);
        if (!node) {
          return;
        }
        if (!checked) {
          updateGroupNode(Array.from(new Set([...groupIds, node.key])));
        } else {
          const flattenChildNodes = node.flattenChildNodes.filter((item) =>
            [...groupIds].includes(item.id),
          );
          const flattenChildNodesCheckKeys: string[] = flattenChildNodes.map((item) => item.key);
          flattenChildNodes.forEach((item) => {
            flattenChildNodesCheckKeys.push(...item.childNodes.map((elem) => elem.key));
          });
          const parents = dataTree.getNode(key)?.parentNodes || [];
          const parentsChecked = parents.filter((elem) => checkedKeysValue.includes(elem.key));
          const otherNodes: TreeNode[] = [];
          parentsChecked.forEach((item) => {
            otherNodes.push(
              ...item.childNodes.filter((elem) => checkedKeysValue.includes(elem.key)),
            );
          });
          const otherIds = otherNodes
            .filter((item) => ![...parentsChecked, node].map((elem) => elem.key).includes(item.key))
            .map((tempNode) => tempNode.key);
          updateGroupNode(
            Array.from(
              new Set([
                ...groupIds.filter(
                  (item) => ![...parents.map((tempItem) => tempItem.key), key].includes(item),
                ),
                ...otherIds,
              ]),
            ),
          );
        }
      }}
    />
  ) : (
    <Empty
      description={<div style={{ color: '#C0C0C0' }}>未找到结果，换个关键词试试吧</div>}
      image={
        <img src="https://hwobs.fanruan.com/ui/search.png" style={{ width: 160, height: 160 }} />
      }
      imageStyle={{ height: 160 }}
    />
  );
};
export default RoleTree;
